import React, { useMemo, useCallback, useState } from 'react';
import { useStore } from 'zustand';
import { useTheme, alpha } from '@mui/material';
import { useUnityBuildStore } from '../../stores/store';
import { useAssemblyStore } from '../../stores/AssemblyStore';
import { useKeyBoardEventStore } from '../../stores/keyEventStore';
import { useDragManager } from './DragManager';
import {
  calculatePolygonArea,
  calculateRealWorldDistance,
  calculateRealWorldArea,
  getAdjustedCursorPosition,
} from './takeoffHelper';
import { normalizeColor } from '../../utils/colorUtils';

interface AreaDrawingToolProps {
  cursorPositionRef: React.MutableRefObject<{ x: number; y: number }>;
}

/**
 * Professional Area Drawing Tool for Construction Drawings
 *
 * Features:
 * - Real-time polygon visualization with professional styling
 * - Live area calculations and measurements
 * - Smart cursor snapping and angle constraints
 * - Professional visual feedback with fills, strokes, and labels
 * - Hover effects and interactive elements
 * - Construction drawing optimized colors and patterns
 */
export const AreaDrawingTool: React.FC<AreaDrawingToolProps> = ({ cursorPositionRef }) => {
  const theme = useTheme();
  const unityBuildStore = useStore(useUnityBuildStore);
  const { currentTakeoffAssembly } = useStore(useAssemblyStore);
  const { snapMode } = useStore(useKeyBoardEventStore);
  const { isDragging: isDraggingPoints, getPointPosition } = useDragManager();

  const { temporaryPoints, angleOptions, userScale, cursorPosition, isAnnotatingTakeoff } =
    unityBuildStore;

  // Local state for enhanced visual feedback
  const [hoveredSegment, setHoveredSegment] = useState<number | null>(null);
  const [showMeasurements] = useState(true);

  // Only render for area type assemblies when actively annotating (creating/editing)
  // The AreaPolygonRenderer handles static display of all area assemblies
  const shouldRender = useMemo(() => {
    return (
      currentTakeoffAssembly?.assemblymeasurementtype === 'area' &&
      isAnnotatingTakeoff &&
      (temporaryPoints?.length > 0 || currentTakeoffAssembly?.pointsList?.length === 0)
    );
  }, [currentTakeoffAssembly, isAnnotatingTakeoff, temporaryPoints]);

  // Get working points (either from current assembly or temporary points during creation)
  const workingPoints = useMemo(() => {
    if (currentTakeoffAssembly?.pointsList?.length) {
      return currentTakeoffAssembly.pointsList;
    }
    return temporaryPoints || [];
  }, [currentTakeoffAssembly, temporaryPoints]);

  // Get visual position for a point (accounting for drag operations)
  const getVisualPointPosition = useCallback(
    (point: any) => {
      if (isDraggingPoints && point.pointid) {
        const dragPosition = getPointPosition(point.pointid);
        if (dragPosition) {
          return { x: dragPosition.x, y: dragPosition.y };
        }
      }
      return { x: point.x, y: point.y };
    },
    [isDraggingPoints, getPointPosition]
  );

  // Calculate real-time area and perimeter
  const measurements = useMemo(() => {
    if (workingPoints.length < 3) {
      return { area: 0, perimeter: 0 };
    }

    const visualPoints = workingPoints.map(getVisualPointPosition);

    // Calculate area using existing helper
    const pixelArea = calculatePolygonArea(visualPoints);
    const realWorldArea = calculateRealWorldArea(pixelArea, userScale);

    // Calculate perimeter
    let perimeter = 0;
    for (let i = 0; i < visualPoints.length; i++) {
      const current = visualPoints[i];
      const next = visualPoints[(i + 1) % visualPoints.length];
      const dx = next.x - current.x;
      const dy = next.y - current.y;
      const segmentLength = Math.sqrt(dx * dx + dy * dy);
      perimeter += segmentLength;
    }
    const realWorldPerimeter = calculateRealWorldDistance(perimeter, userScale);

    return {
      area: realWorldArea,
      perimeter: realWorldPerimeter,
    };
  }, [workingPoints, getVisualPointPosition, userScale]);

  // Get adjusted cursor position with snapping
  const adjustedCursorPosition = useMemo(() => {
    if (!snapMode || workingPoints.length === 0) {
      return cursorPosition;
    }

    const lastPoint = workingPoints[workingPoints.length - 1];
    if (!lastPoint) return cursorPosition;

    return getAdjustedCursorPosition(
      getVisualPointPosition(lastPoint),
      cursorPosition,
      angleOptions,
      snapMode,
      currentTakeoffAssembly
    );
  }, [
    cursorPosition,
    workingPoints,
    snapMode,
    angleOptions,
    currentTakeoffAssembly,
    getVisualPointPosition,
  ]);

  // Professional color scheme for construction drawings
  const colors = useMemo(() => {
    const baseColor = normalizeColor(currentTakeoffAssembly?.linecolor, theme.palette.primary.main);
    const fillColor = normalizeColor(
      currentTakeoffAssembly?.pointfillcolor,
      theme.palette.primary.light
    );

    return {
      stroke: baseColor,
      fill: alpha(fillColor, 0.15),
      fillHover: alpha(fillColor, 0.25),
      strokeWidth: currentTakeoffAssembly?.linesize || 2,
      dashArray: currentTakeoffAssembly?.linedasharray || '',
      measurements: theme.palette.text.primary,
      measurementsBg: alpha(theme.palette.background.paper, 0.9),
    };
  }, [currentTakeoffAssembly, theme]);

  // Render the main polygon fill and stroke
  const renderPolygon = useMemo(() => {
    if (workingPoints.length < 3) return null;

    const visualPoints = workingPoints.map(getVisualPointPosition);
    const pathData =
      visualPoints
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
        .join(' ') + ' Z';

    return (
      <g key="area-polygon">
        {/* Main filled polygon */}
        <path
          d={pathData}
          fill={colors.fill}
          stroke={colors.stroke}
          strokeWidth={colors.strokeWidth}
          strokeDasharray={colors.dashArray}
          strokeLinejoin="round"
          strokeLinecap="round"
          style={{
            transition: 'fill 0.2s ease-in-out',
          }}
          onMouseEnter={() => setHoveredSegment(-1)} // -1 indicates polygon hover
          onMouseLeave={() => setHoveredSegment(null)}
        />

        {/* Hover effect overlay */}
        {hoveredSegment === -1 && (
          <path
            d={pathData}
            fill={colors.fillHover}
            stroke="none"
            pointerEvents="none"
            style={{
              transition: 'opacity 0.2s ease-in-out',
            }}
          />
        )}
      </g>
    );
  }, [workingPoints, getVisualPointPosition, colors, hoveredSegment]);

  // Render individual segments with hover effects
  const renderSegments = useMemo(() => {
    if (workingPoints.length < 2) return null;

    const visualPoints = workingPoints.map(getVisualPointPosition);
    const segments = [];

    for (let i = 0; i < visualPoints.length; i++) {
      const current = visualPoints[i];
      const next = visualPoints[(i + 1) % visualPoints.length];

      // Don't render the closing segment if we have less than 3 points
      if (i === visualPoints.length - 1 && visualPoints.length < 3) break;

      const isHovered = hoveredSegment === i;

      segments.push(
        <line
          key={`segment-${i}`}
          x1={current.x}
          y1={current.y}
          x2={next.x}
          y2={next.y}
          stroke={isHovered ? theme.palette.secondary.main : colors.stroke}
          strokeWidth={isHovered ? colors.strokeWidth + 1 : colors.strokeWidth}
          strokeDasharray={colors.dashArray}
          strokeLinecap="round"
          style={{
            transition: 'stroke 0.2s ease-in-out, stroke-width 0.2s ease-in-out',
            cursor: 'pointer',
          }}
          onMouseEnter={() => setHoveredSegment(i)}
          onMouseLeave={() => setHoveredSegment(null)}
        />
      );
    }

    return <g key="area-segments">{segments}</g>;
  }, [workingPoints, getVisualPointPosition, colors, hoveredSegment, theme]);

  // Render trailing line from last point to cursor
  const renderTrailingLine = useMemo(() => {
    if (workingPoints.length === 0 || isDraggingPoints) return null;

    const lastPoint = workingPoints[workingPoints.length - 1];
    if (!lastPoint) return null;

    const lastVisualPoint = getVisualPointPosition(lastPoint);
    const targetPosition = adjustedCursorPosition;

    return (
      <line
        key="trailing-line"
        x1={lastVisualPoint.x}
        y1={lastVisualPoint.y}
        x2={targetPosition.x}
        y2={targetPosition.y}
        stroke={colors.stroke}
        strokeWidth={1}
        strokeDasharray="4,4"
        opacity={0.6}
        pointerEvents="none"
      />
    );
  }, [workingPoints, getVisualPointPosition, adjustedCursorPosition, colors, isDraggingPoints]);

  // Render closing line preview (from cursor to first point when we have 2+ points)
  const renderClosingPreview = useMemo(() => {
    if (workingPoints.length < 2 || isDraggingPoints) return null;

    const firstPoint = workingPoints[0];
    if (!firstPoint) return null;

    const firstVisualPoint = getVisualPointPosition(firstPoint);
    const targetPosition = adjustedCursorPosition;

    return (
      <line
        key="closing-preview"
        x1={targetPosition.x}
        y1={targetPosition.y}
        x2={firstVisualPoint.x}
        y2={firstVisualPoint.y}
        stroke={colors.stroke}
        strokeWidth={1}
        strokeDasharray="2,6"
        opacity={0.4}
        pointerEvents="none"
      />
    );
  }, [workingPoints, getVisualPointPosition, adjustedCursorPosition, colors, isDraggingPoints]);

  // Render area measurement label
  const renderAreaLabel = useMemo(() => {
    if (!showMeasurements || measurements.area === 0) return null;

    // Calculate centroid for label placement
    const visualPoints = workingPoints.map(getVisualPointPosition);
    if (visualPoints.length < 3) return null;

    let centroidX = 0;
    let centroidY = 0;
    visualPoints.forEach(point => {
      centroidX += point.x;
      centroidY += point.y;
    });
    centroidX /= visualPoints.length;
    centroidY /= visualPoints.length;

    const areaText = `${measurements.area.toFixed(2)} ft²`;
    const perimeterText = `${measurements.perimeter.toFixed(2)} ft`;

    return (
      <g key="area-measurements">
        {/* Background for better readability */}
        <rect
          x={centroidX - 40}
          y={centroidY - 25}
          width={80}
          height={35}
          fill={colors.measurementsBg}
          stroke={colors.stroke}
          strokeWidth={1}
          rx={4}
          ry={4}
        />

        {/* Area text */}
        <text
          x={centroidX}
          y={centroidY - 8}
          textAnchor="middle"
          fill={colors.measurements}
          fontSize="12"
          fontWeight="600"
          fontFamily="monospace"
        >
          {areaText}
        </text>

        {/* Perimeter text */}
        <text
          x={centroidX}
          y={centroidY + 6}
          textAnchor="middle"
          fill={colors.measurements}
          fontSize="10"
          opacity={0.8}
          fontFamily="monospace"
        >
          {perimeterText}
        </text>
      </g>
    );
  }, [showMeasurements, measurements, workingPoints, getVisualPointPosition, colors]);

  // Render snap indicators
  const renderSnapIndicators = useMemo(() => {
    if (!snapMode || workingPoints.length === 0) return null;

    const targetPosition = adjustedCursorPosition;

    return (
      <g key="snap-indicators">
        <circle
          cx={targetPosition.x}
          cy={targetPosition.y}
          r={4}
          fill="none"
          stroke={theme.palette.warning.main}
          strokeWidth={2}
          opacity={0.8}
        />
        <circle
          cx={targetPosition.x}
          cy={targetPosition.y}
          r={8}
          fill="none"
          stroke={theme.palette.warning.main}
          strokeWidth={1}
          strokeDasharray="2,2"
          opacity={0.6}
        />
      </g>
    );
  }, [snapMode, workingPoints, adjustedCursorPosition, theme]);

  if (!shouldRender) return null;

  return (
    <g key="area-drawing-tool">
      {renderPolygon}
      {renderSegments}
      {renderTrailingLine}
      {renderClosingPreview}
      {renderAreaLabel}
      {renderSnapIndicators}
    </g>
  );
};
